/**
 * Lógica de classificação de retenção para o módulo do Personal.
 *
 * Regras (definidas pelo usuário 2026-05-15):
 *   - Engajado:   > 3 treinos nos últimos 7 dias
 *   - Oscilando:  1-3 treinos nos últimos 7 dias
 *   - Em risco:   sem treinar há 5-29 dias
 *   - Abandono:   sem treinar há 30+ dias (ou nunca treinou)
 *   - Recuperado: treinou nos últimos 7 dias E gap anterior ≥21 dias
 *
 * Avaliação priorizada: Abandono > Recuperado > Engajado > Oscilando > Em risco.
 */

export type RetentionCategory =
  | 'engajado'
  | 'oscilando'
  | 'em_risco'
  | 'abandono'
  | 'recuperado';

export interface SessionLite {
  completed_at: string; // ISO
}

const DAY_MS = 86_400_000;

/**
 * Classifica um aluno baseado nas suas sessions.
 * `referenceDate` permite calcular categoria histórica (pra gráficos de tendência).
 */
export function classifyStudent(
  sessions: SessionLite[],
  referenceDate: Date = new Date()
): RetentionCategory {
  if (!sessions || sessions.length === 0) return 'abandono';

  const sorted = sessions
    .filter(s => s.completed_at)
    .map(s => ({ t: new Date(s.completed_at).getTime() }))
    .sort((a, b) => b.t - a.t);

  if (sorted.length === 0) return 'abandono';

  const ref = referenceDate.getTime();
  const lastT = sorted[0].t;
  const daysSinceLast = (ref - lastT) / DAY_MS;

  // Abandono: ≥30 dias sem treinar
  if (daysSinceLast >= 30) return 'abandono';

  // Recuperado: treinou nos últimos 7 dias E gap entre último e penúltimo ≥21 dias
  if (daysSinceLast <= 7 && sorted.length >= 2) {
    const gapBefore = (lastT - sorted[1].t) / DAY_MS;
    if (gapBefore >= 21) return 'recuperado';
  }

  // Conta treinos nos últimos 7 dias
  const sevenDaysAgo = ref - 7 * DAY_MS;
  const lastWeekCount = sorted.filter(s => s.t >= sevenDaysAgo).length;

  if (lastWeekCount > 3) return 'engajado';
  if (lastWeekCount >= 1) return 'oscilando';

  // 0 treinos nos últimos 7 dias e <30 dias sem treinar = Em risco
  return 'em_risco';
}

/** Dias desde a última session (null se nunca treinou). */
export function daysSinceLastSession(
  sessions: SessionLite[],
  referenceDate: Date = new Date()
): number | null {
  if (!sessions || sessions.length === 0) return null;
  const last = sessions
    .filter(s => s.completed_at)
    .reduce<number | null>((acc, s) => {
      const t = new Date(s.completed_at).getTime();
      return acc === null || t > acc ? t : acc;
    }, null);
  if (last === null) return null;
  return Math.floor((referenceDate.getTime() - last) / DAY_MS);
}

// ─── Labels visuais ────────────────────────────────────────────────────────

export const CATEGORY_LABEL: Record<RetentionCategory, string> = {
  engajado: 'Engajado',
  oscilando: 'Oscilando',
  em_risco: 'Em risco',
  abandono: 'Abandono',
  recuperado: 'Recuperado',
};

export const CATEGORY_COLOR: Record<RetentionCategory, string> = {
  engajado: '#2E7D32',
  oscilando: '#F9A825',
  em_risco: '#FB8C00',
  abandono: '#C8102E',
  recuperado: '#1976D2',
};

/** Ordem visual padrão (do mais "positivo" pro mais "preocupante"). */
export const CATEGORY_ORDER: RetentionCategory[] = [
  'engajado',
  'oscilando',
  'em_risco',
  'abandono',
  'recuperado',
];

export const CATEGORY_DESCRIPTION: Record<RetentionCategory, string> = {
  engajado: 'Treina de forma consistente, mais de 3 vezes por semana.',
  oscilando: 'Alterna entre treinos e pequenas pausas, com menos de 3 treinos por semana.',
  em_risco: 'Está entre 5 e 29 dias sem treinar.',
  abandono: 'Está há 30 dias ou mais sem treinar.',
  recuperado: 'Retomou os treinos na semana atual após um período de abandono.',
};
